import type { DateRange, DateValue, PresetOption } from '../types';
import type {
  ComparisonPresetDefinition,
  DailyPresetId,
  MainPresetDefinition,
  MonthlyPresetId,
  WeeklyPresetId,
} from './types';
import { DAILY_COMPARISON_REGISTRY, DAILY_PRESET_REGISTRY } from './registries/daily';
import { BenchmarkingDateValue } from 'components/filter/modules/benchmarking';
import {
  WEEKLY_COMPARISON_REGISTRY,
  WEEKLY_PRESET_REGISTRY,
} from 'components/filter/input/DateRangePicker/presets/registries/weekly';
import {
  MONTHLY_COMPARISON_REGISTRY,
  MONTHLY_PRESET_REGISTRY,
} from 'components/filter/input/DateRangePicker/presets/registries/monthly';

export function buildPresetOptions<TId extends string>(
  ids: TId[],
  registry: Record<TId, MainPresetDefinition<TId>>,
  today: Date
): PresetOption[] {
  return ids.map(id => {
    const def = registry[id];
    const range = def.calculate(today);
    return { value: id, label: def.labelKey, from: range?.from, to: range?.to };
  });
}

export function buildComparisonOptions<TId extends string>(
  ids: TId[],
  registry: Record<TId, ComparisonPresetDefinition<TId>>,
  today: Date,
  mainRange: DateRange
): PresetOption[] {
  return ids.map(id => {
    const def = registry[id];
    const range = def.calculate(today, mainRange);
    return { value: id, label: def.labelKey, from: range?.from, to: range?.to };
  });
}

export function recalcComparison(previousRange: DateValue, newRange: DateValue): DateValue['comparison'];

export function recalcComparison<TId extends string>(
  previousRange: DateValue,
  newRange: DateValue,
  registry: Record<TId, ComparisonPresetDefinition<TId>>,
  fallbackPresetId: TId
): DateValue['comparison'];

export function recalcComparison<TId extends string>(
  previousRange: DateValue,
  newRange: DateValue,
  registry?: Record<TId, ComparisonPresetDefinition<TId>>,
  fallbackPresetId?: TId
): DateValue['comparison'] {
  const today = new Date();
  const currentPreset = previousRange.comparison?.presetValue;

  const effectiveRegistry = registry ?? (DAILY_COMPARISON_REGISTRY as Record<TId, ComparisonPresetDefinition<TId>>);
  const effectiveFallback = fallbackPresetId ?? ('previous_year_by_cal_week' as TId);

  const presetId: TId =
    currentPreset === 'custom' || !currentPreset || !(currentPreset in effectiveRegistry)
      ? effectiveFallback
      : (currentPreset as TId);

  const [resolved] = buildComparisonOptions([presetId], effectiveRegistry, today, newRange.range);

  if (!resolved?.from || !resolved?.to) {
    return { presetValue: 'no_comparison' };
  }

  return { presetValue: resolved.value, from: resolved.from, to: resolved.to };
}

export const recalcDatesFromPresets = (dateValue: DateValue) => {
  const newDateValue = { ...dateValue };

  switch ((dateValue as BenchmarkingDateValue).temporalType) {
    case 'WEEKLY':
      newDateValue.range =
        calculateDateRangeFromPresets(newDateValue.range, WEEKLY_PRESET_REGISTRY) ?? newDateValue.range;
      newDateValue.comparison = calculateComparisonRangeFromPresets(
        newDateValue.comparison,
        newDateValue.range,
        WEEKLY_COMPARISON_REGISTRY
      );
      break;
    case 'MONTHLY':
      newDateValue.range =
        calculateDateRangeFromPresets(newDateValue.range, MONTHLY_PRESET_REGISTRY) ?? newDateValue.range;
      newDateValue.comparison = calculateComparisonRangeFromPresets(
        newDateValue.comparison,
        newDateValue.range,
        MONTHLY_COMPARISON_REGISTRY
      );
      break;
    default:
      newDateValue.range =
        calculateDateRangeFromPresets(newDateValue.range, DAILY_PRESET_REGISTRY) ?? newDateValue.range;
      newDateValue.comparison = calculateComparisonRangeFromPresets(
        newDateValue.comparison,
        newDateValue.range,
        DAILY_COMPARISON_REGISTRY
      );
  }
  return newDateValue;
};

type PresetId = DailyPresetId | WeeklyPresetId | MonthlyPresetId;

export const calculateDateRangeFromPresets = <T extends PresetId>(
  range: DateRange,
  registry: Record<T, MainPresetDefinition<T>>
) => {
  const newRange = { ...range };
  if (newRange.presetValue) {
    const definition = registry[newRange.presetValue as T];
    const recalculatedDates = definition.calculate(new Date());

    // only update the from/to if we have recalculated dates, custom dates return and will stay as beforehand
    if (recalculatedDates) {
      newRange.from = recalculatedDates.from;
      newRange.to = recalculatedDates.to;
    }
  }
  return newRange;
};

export const calculateComparisonRangeFromPresets = <T extends string>(
  comparisonRange: DateRange | undefined,
  baseRange: DateRange,
  registry: Record<T, ComparisonPresetDefinition<T>>
) => {
  if (!comparisonRange) return undefined;
  const newComparisonRange = { ...comparisonRange };
  if (newComparisonRange.presetValue) {
    const definition = registry[newComparisonRange.presetValue as T];
    const recalculatedDates = definition.calculate(new Date(), baseRange);

    // only update the from/to if we have recalculated dates, custom dates return and will stay as beforehand
    if (recalculatedDates) {
      newComparisonRange.from = recalculatedDates.from;
      newComparisonRange.to = recalculatedDates.to;
    }
  }
  return newComparisonRange;
};
